import React, { useLayoutEffect, useRef, useState } from 'react'
import { formatPaymentDate } from '../../utils/utils'
import { User, VoucherType } from '../../types'

type StageState = 'done' | 'current' | 'upcoming' | 'rejected' | 'voided'

const COLORS: Record<StageState, { fg: string; bg: string }> = {
  done: { fg: '#1F7A5C', bg: '#E3F1EA' },
  current: { fg: '#A8710E', bg: '#FBF0DC' },
  upcoming: { fg: '#8B968C', bg: '#EDEFEA' },
  rejected: { fg: '#AF3A2E', bg: '#F8E7E4' },
  voided: { fg: '#8B968C', bg: 'transparent' },
}

type DisplayStage = {
  key: string
  kindLabel: string
  compactTopLabel: string
  tooltip: string
  name: string
  dateRaw?: string | number | null
  dateFull?: string
  email?: string
  comment?: string | null
  avatarUser?: User
  stageState: StageState
  showAwaitingPill?: boolean
  voidedNote?: boolean
}

// Horizontal (full variant) only when every stage gets at least this much
// room; otherwise the stages stack into a vertical timeline, so the row
// never needs a horizontal scrollbar.
const MIN_STAGE_WIDTH = 170
const CONNECTOR_LENGTH = 24
// Cards in the vertical timeline keep one readable width instead of
// stretching across the whole column.
const VERTICAL_CARD_WIDTH = 360

const connectorBackground = (
  state: 'done' | 'voided' | 'plain',
  direction: 'right' | 'bottom',
) =>
  state === 'done'
    ? COLORS.done.fg
    : state === 'voided'
      ? `repeating-linear-gradient(to ${direction}, ${COLORS.rejected.fg} 0 5px, transparent 5px 10px)`
      : COLORS.upcoming.fg

const getInitials = (user?: User) => {
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`
  return initials.toUpperCase() || '?'
}

const StatusIcon = ({ state }: { state: StageState }) => {
  const stroke = '#FFFFFF'
  const commonProps = {
    width: 10,
    height: 10,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 2.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  if (state === 'done') {
    return (
      <svg {...commonProps}>
        <polyline points="4,13 9,18 20,6" />
      </svg>
    )
  }
  if (state === 'current') {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8.5" />
        <polyline points="12,7 12,12 15.5,14" />
      </svg>
    )
  }
  if (state === 'rejected') {
    return (
      <svg {...commonProps}>
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </svg>
    )
  }
  return (
    <svg {...commonProps}>
      <circle cx="12" cy="12" r="1.5" fill={stroke} stroke="none" />
    </svg>
  )
}

const ApprovalStages = ({
  voucher,
  variant = 'full',
}: {
  voucher: VoucherType
  variant?: 'compact' | 'full'
}) => {
  const approvals = voucher?.voucher_approvals || []
  const isCompact = variant === 'compact'

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el || isCompact) return
    setContainerWidth(el.getBoundingClientRect().width)
    const observer = new ResizeObserver(([entry]) =>
      setContainerWidth(entry.contentRect.width),
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isCompact])

  const sortedApprovals = [...approvals].sort(
    (a, b) => a.order_number - b.order_number,
  )

  const rejectedIndex = sortedApprovals.findIndex(
    (a) => a.status === 'REJECTED',
  )
  const firstPendingIndex = sortedApprovals.findIndex(
    (a) =>
      a.status !== 'APPROVED' && a.status !== 'PAID' && a.status !== 'REJECTED',
  )

  const stages: DisplayStage[] = []

  const creator = voucher?.created_by
  if (creator?.id) {
    const creatorName =
      `${creator.first_name || ''} ${creator.last_name || ''}`.trim()
    const submittedDate = voucher.submitted_at || voucher.created_at
    const creationKind = voucher.submitted_at ? 'Submitted' : 'Created'
    stages.push({
      key: 'created',
      kindLabel: creationKind,
      compactTopLabel: creationKind,
      tooltip: `${creationKind} by ${creatorName || 'Unknown'}`,
      name: creatorName || 'Unknown',
      email: creator?.email || '',
      dateRaw: submittedDate,
      dateFull: submittedDate
        ? `${creationKind} ${formatPaymentDate(submittedDate)}`
        : undefined,
      avatarUser: creator,
      stageState: 'done',
    })
  }

  sortedApprovals.forEach((approval, index) => {
    const { approver, approval_stage, status, approved_at, comment } = approval

    const labelPrefix = approval_stage?.is_payment_stage
      ? 'Payment'
      : 'Approval'
    const shortRole = approver?.user_role?.name || 'Unknown'
    const roleName = `${shortRole} (${approver?.branch || '-'})`
    const approverName = approver
      ? `${approver.first_name || ''} ${approver.last_name || ''}`.trim()
      : ''

    let stageState: StageState
    if (status === 'APPROVED' || status === 'PAID') {
      stageState = 'done'
    } else if (status === 'REJECTED') {
      stageState = 'rejected'
    } else if (rejectedIndex !== -1 && index > rejectedIndex) {
      stageState = 'voided'
    } else if (index === firstPendingIndex) {
      stageState = 'current'
    } else {
      stageState = 'upcoming'
    }

    stages.push({
      key: `approval-${approval.id || index}`,
      kindLabel: labelPrefix,
      compactTopLabel: `${labelPrefix} · ${shortRole}`,
      tooltip: roleName,
      name: approverName,
      dateRaw: approved_at,
      dateFull: approved_at
        ? `${status === 'REJECTED' ? 'Rejected' : 'Approved'} ${formatPaymentDate(approved_at)}`
        : undefined,
      email: approver?.email || '',
      comment,
      avatarUser: approver,
      stageState,
      showAwaitingPill: stageState === 'current',
      voidedNote: stageState === 'voided',
    })
  })

  if (stages.length === 0) return null

  const rejectedDisplayIndex = stages.findIndex(
    (s) => s.stageState === 'rejected',
  )

  const avatarSize = isCompact ? 32 : 52
  const badgeSize = 19

  const horizontalWidthNeeded =
    stages.length * MIN_STAGE_WIDTH + (stages.length - 1) * CONNECTOR_LENGTH
  const isVertical =
    !isCompact &&
    containerWidth !== null &&
    containerWidth < horizontalWidthNeeded

  return (
    <div ref={containerRef} style={{ padding: '4px 4px', margin: '8px 0' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: isVertical ? 'column' : 'row',
          flexWrap: isCompact ? 'wrap' : 'nowrap',
          alignItems: isCompact ? 'center' : isVertical ? 'stretch' : 'flex-start',
          justifyContent: isCompact ? 'flex-start' : undefined,
        }}
      >
        {stages.map((stage, index) => {
          const {
            kindLabel,
            compactTopLabel,
            tooltip,
            name,
            dateRaw,
            dateFull,
            comment,
            avatarUser,
            stageState,
            showAwaitingPill,
            voidedNote,
          } = stage

          const colors = COLORS[stageState]
          const isVoided = stageState === 'voided'

          const connectorState: 'done' | 'voided' | 'plain' =
            stageState === 'done'
              ? 'done'
              : rejectedDisplayIndex !== -1 && index >= rejectedDisplayIndex
                ? 'voided'
                : 'plain'

          const avatar = (
            <div
              style={{
                position: 'relative',
                width: avatarSize,
                height: avatarSize,
                flex: 'none',
              }}
            >
              <div
                style={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 600,
                  border: '2px solid #FFFFFF',
                  boxShadow: `0 0 0 ${stageState === 'current' ? 2 : 1.5}px ${colors.fg}`,
                  backgroundColor: colors.bg,
                  color: colors.fg,
                }}
              >
                {getInitials(avatarUser)}
              </div>
              {!isVoided && (
                <div
                  style={{
                    position: 'absolute',
                    right: -3,
                    bottom: -3,
                    width: badgeSize,
                    height: badgeSize,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid #FFFFFF',
                    backgroundColor: colors.fg,
                  }}
                >
                  <StatusIcon state={stageState} />
                </div>
              )}
            </div>
          )

          const card = (
            <div
              style={{
                borderRadius: 10,
                padding: '14px 14px 15px',
                width: '100%',
                backgroundColor: colors.bg,
                border: `1px solid ${stageState === 'current' ? colors.fg : 'transparent'}`,
                borderStyle: isVoided ? 'dashed' : 'solid',
                borderColor: isVoided ? '#dee2e6' : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                textAlign: isVertical ? 'left' : 'center',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: colors.fg,
                }}
              >
                {kindLabel}
              </div>
              {name && (
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: '#212529',
                  }}
                >
                  {name}
                </div>
              )}
              {dateFull && (
                <div style={{ fontSize: 12.5, color: '#6c757d' }}>
                  {dateFull}
                </div>
              )}
              {comment && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: '#212529',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 6,
                    padding: '8px 10px',
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <strong style={{ color: '#6c757d' }}>Comment</strong>{' '}
                  {comment}
                </div>
              )}
              {showAwaitingPill && (
                <span
                  style={{
                    alignSelf: isVertical ? 'flex-start' : 'center',
                    marginTop: 2,
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '.03em',
                    textTransform: 'uppercase',
                    color: '#FFFFFF',
                    backgroundColor: colors.fg,
                    padding: '4px 10px',
                    borderRadius: 100,
                  }}
                >
                  Awaiting action
                </span>
              )}
              {voidedNote && (
                <div
                  style={{
                    fontSize: 12.5,
                    fontStyle: 'italic',
                    color: '#6c757d',
                  }}
                >
                  Not reached — returned to submitter
                </div>
              )}
            </div>
          )

          return (
            <React.Fragment key={`approval-fragment-${stage.key}`}>
              {isCompact ? (
                <div
                  title={tooltip}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flex: '0 0 auto',
                    backgroundColor: colors.bg,
                    border: `1px solid ${stageState === 'current' ? colors.fg : 'transparent'}`,
                    borderRadius: 100,
                    padding: '7px 14px 7px 7px',
                    opacity: isVoided ? 0.55 : 1,
                  }}
                >
                  <div
                    style={{
                      width: avatarSize,
                      height: avatarSize,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                      fontSize: 12.5,
                      fontWeight: 700,
                      border: '1.5px solid #FFFFFF',
                      backgroundColor: '#FFFFFF',
                      color: colors.fg,
                    }}
                  >
                    {getInitials(avatarUser)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
                    <span
                      style={{
                        fontSize: 14.5,
                        fontWeight: 600,
                        color: '#212529',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name || 'Unassigned'}
                    </span>
                    <span style={{ fontSize: 13, color: '#6c757d', whiteSpace: 'nowrap' }}>
                      {compactTopLabel}
                      {dateRaw ? ` · ${formatPaymentDate(dateRaw)}` : ''}
                    </span>
                  </div>
                  {showAwaitingPill && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '.03em',
                        textTransform: 'uppercase',
                        color: '#FFFFFF',
                        backgroundColor: colors.fg,
                        padding: '3px 9px',
                        borderRadius: 100,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Awaiting
                    </span>
                  )}
                </div>
              ) : isVertical ? (
                <div style={{ display: 'flex', gap: 14 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: 'none',
                      width: avatarSize,
                    }}
                  >
                    <div style={{ opacity: isVoided ? 0.55 : 1 }}>{avatar}</div>
                    {index < stages.length - 1 && (
                      <div
                        style={{
                          flex: 1,
                          width: 2,
                          minHeight: 14,
                          margin: '8px 0',
                          borderRadius: 2,
                          background: connectorBackground(
                            connectorState,
                            'bottom',
                          ),
                          opacity: connectorState === 'voided' ? 0.55 : 1,
                        }}
                      />
                    )}
                  </div>
                  <div
                    style={{
                      flex: `0 1 ${VERTICAL_CARD_WIDTH}px`,
                      minWidth: 0,
                      paddingBottom: index < stages.length - 1 ? 14 : 0,
                      opacity: isVoided ? 0.55 : 1,
                    }}
                  >
                    {card}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    maxWidth: 260,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                    padding: '0 6px',
                    opacity: isVoided ? 0.55 : 1,
                  }}
                >
                  {avatar}
                  {card}
                </div>
              )}

              {index < stages.length - 1 && isCompact && (
                <span
                  style={{
                    flex: '0 0 auto',
                    color: '#c9d0d3',
                    fontSize: 16,
                  }}
                >
                  →
                </span>
              )}

              {index < stages.length - 1 && !isCompact && !isVertical && (
                <div
                  style={{
                    flex: `0 0 ${CONNECTOR_LENGTH}px`,
                    minWidth: CONNECTOR_LENGTH,
                    height: 2,
                    marginTop: avatarSize / 2 - 1,
                    borderRadius: 2,
                    alignSelf: 'flex-start',
                    background: connectorBackground(connectorState, 'right'),
                    opacity: connectorState === 'voided' ? 0.55 : 1,
                  }}
                />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default ApprovalStages
